import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FiArrowRight,
  FiCopy,
  FiCornerDownLeft,
  FiDownload,
  FiFolder,
  FiHash,
  FiMail,
  FiMoon,
  FiSearch,
  FiSun
} from "react-icons/fi";
import { FaGithub, FaLinkedinIn } from "react-icons/fa";
import { navLinks } from "../nav/Nav";
import { useTheme } from "../../context/ThemeContext";
import useCopyEmail from "../../hooks/useCopyEmail";
import { profile } from "../../data/resume";
import { projects } from "../../data/projects";
import { ease } from "../../utils/motionPresets";
import CV from "../../assets/Yaseen-Al-Midani.pdf";
import "./command.css";

const openExternal = (url) => window.open(url, "_blank", "noopener,noreferrer");

const downloadCV = () => {
  const link = document.createElement("a");
  link.href = CV;
  link.download = "Yaseen-Al-Midani-CV.pdf";
  link.click();
};

// Every character of the query must appear in order (e.g. "gtb" → "GitHub").
const fuzzyMatch = (text, query) => {
  let i = 0;
  const haystack = text.toLowerCase();
  for (const char of query) {
    i = haystack.indexOf(char, i);
    if (i === -1) return false;
    i += 1;
  }
  return true;
};

const CommandPalette = ({ open, onClose }) => {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const copyEmail = useCopyEmail();
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const returnFocus = useRef(null);

  const commands = useMemo(
    () => [
      ...navLinks.map((link) => ({
        id: `nav-${link.id}`,
        group: "Navigate",
        label: `Go to ${link.label}`,
        Icon: FiHash,
        run: () => navigate(`/#${link.id}`)
      })),
      { id: "copy-email", group: "Actions", label: "Copy email address", hint: profile.email, Icon: FiCopy, run: copyEmail },
      { id: "send-email", group: "Actions", label: "Send an email", Icon: FiMail, run: () => (window.location.href = `mailto:${profile.email}`) },
      { id: "cv", group: "Actions", label: "Download CV", hint: "PDF", Icon: FiDownload, run: downloadCV },
      {
        id: "theme",
        group: "Actions",
        label: `Switch to ${theme === "dark" ? "light" : "dark"} theme`,
        keywords: "theme mode dark light appearance",
        Icon: theme === "dark" ? FiSun : FiMoon,
        run: toggleTheme
      },
      ...projects.map((project) => ({
        id: `project-${project.id}`,
        group: "Projects",
        label: `${project.title} case study`,
        hint: project.category,
        keywords: project.stack.map((item) => item.name).join(" "),
        Icon: FiFolder,
        run: () => navigate(`/work/${project.id}`)
      })),
      { id: "linkedin", group: "Social", label: "LinkedIn", Icon: FaLinkedinIn, run: () => openExternal(profile.linkedin) },
      { id: "github", group: "Social", label: "GitHub", hint: "@yaseenmi", Icon: FaGithub, run: () => openExternal(profile.github) }
    ],
    [copyEmail, navigate, theme, toggleTheme]
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/\s+/g, "");
    if (!q) return commands;
    return commands.filter(
      (cmd) =>
        `${cmd.label} ${cmd.group} ${cmd.keywords || ""}`.toLowerCase().replace(/\s+/g, "").includes(q) ||
        fuzzyMatch(cmd.label, q)
    );
  }, [commands, query]);

  useEffect(() => {
    if (open) {
      returnFocus.current = document.activeElement;
      setQuery("");
      setCursor(0);
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      document.body.style.overflow = "";
      returnFocus.current?.focus?.();
    }
  }, [open]);

  useEffect(() => setCursor(0), [query]);

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${cursor}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  const execute = (cmd) => {
    if (!cmd) return;
    onClose();
    // Let the dialog unmount (and restore scrolling) before acting.
    setTimeout(cmd.run, 60);
  };

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setCursor((c) => (c + 1) % Math.max(results.length, 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setCursor((c) => (c - 1 + results.length) % Math.max(results.length, 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      execute(results[cursor]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    } else if (event.key === "Tab") {
      event.preventDefault();
    }
  };

  let lastGroup = null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="palette"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onMouseDown={(event) => event.target === event.currentTarget && onClose()}
        >
          <motion.div
            className="palette__panel"
            role="dialog"
            aria-modal="true"
            aria-label="Command menu"
            initial={{ opacity: 0, scale: 0.96, y: -12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: -6 }}
            transition={{ duration: 0.3, ease }}
            onKeyDown={onKeyDown}
          >
            <div className="palette__search">
              <FiSearch aria-hidden="true" />
              <input
                ref={inputRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Type a command or search…"
                aria-label="Search commands"
                role="combobox"
                aria-expanded="true"
                aria-controls="palette-list"
                aria-activedescendant={results[cursor] ? `cmd-${results[cursor].id}` : undefined}
                autoComplete="off"
                spellCheck="false"
              />
              <kbd>esc</kbd>
            </div>

            <ul className="palette__list" id="palette-list" role="listbox" ref={listRef}>
              {results.length === 0 && <li className="palette__empty">No results for “{query}”</li>}
              {results.map((cmd, i) => {
                const header = cmd.group !== lastGroup ? cmd.group : null;
                lastGroup = cmd.group;
                const { Icon } = cmd;
                return (
                  <React.Fragment key={cmd.id}>
                    {header && (
                      <li className="palette__group" role="presentation">
                        {header}
                      </li>
                    )}
                    <li
                      id={`cmd-${cmd.id}`}
                      role="option"
                      aria-selected={i === cursor}
                      data-index={i}
                      className={`palette__item ${i === cursor ? "is-active" : ""}`}
                      onMouseMove={() => i !== cursor && setCursor(i)}
                      onClick={() => execute(cmd)}
                    >
                      <span className="palette__icon">
                        <Icon />
                      </span>
                      <span className="palette__label">{cmd.label}</span>
                      {cmd.hint && <span className="palette__hint mono">{cmd.hint}</span>}
                      <FiArrowRight className="palette__go" aria-hidden="true" />
                    </li>
                  </React.Fragment>
                );
              })}
            </ul>

            <div className="palette__footer mono">
              <span>
                <kbd>↑</kbd>
                <kbd>↓</kbd> navigate
              </span>
              <span>
                <kbd>
                  <FiCornerDownLeft />
                </kbd>{" "}
                select
              </span>
              <span className="palette__brand">yaseenalmidani.com</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CommandPalette;
